// src/components/verification/IdentityVerificationModal.jsx
// Reusable modal version of IdentityVerification.
// Used in: Settings > Profile > Secondary Verification  AND  Wallet page Upload-ID buttons.
// The onboarding flow continues to use its own in-flow design (not this modal).

import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import {
  Upload, CheckCircle, AlertCircle, FileText,
  Shield, X, Info
} from 'lucide-react';
import Button from '../ui/Button';

const acceptedIDTypes = [
  { value: 'passport',         label: 'International Passport',  icon: '🛂' },
  { value: 'national_id',      label: 'National ID Card',         icon: '🪪' },
  { value: 'drivers_license',  label: "Driver's License",         icon: '🚗' },
];

/**
 * Props:
 *   isOpen    {boolean}   – controls visibility
 *   onClose   {function}  – called when user dismisses
 *   onSuccess {function}  – called after a successful submission (approved OR pending manual)
 */
const IdentityVerificationModal = ({ isOpen, onClose, onSuccess }) => {
  const { user, refreshUser } = useAuth();

  const [step, setStep]               = useState(1); // 1: form, 2: success/pending
  const [loading, setLoading]         = useState(false);
  const [idType, setIdType]           = useState('');
  const [frontImage, setFrontImage]   = useState(null);
  const [backImage, setBackImage]     = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview]   = useState(null);
  const [idNumber, setIdNumber]       = useState('');
  const [fullName, setFullName]       = useState('');
  const [errors, setErrors]           = useState({});
  const [resultStatus, setResultStatus] = useState(null); // 'approved' | 'pending'

  // ── Guards ──────────────────────────────────────────────────────────────
  // TEMPORARY: ID upload paused pending Ghana NIA API integration approval.
  // Remove this flag and its guard block below to re-enable once NIA access
  // is confirmed live — see /areas/nia-id-verification-compliance.md.
  const idVerificationTemporarilyPaused = true;

  const isBasicVerified =
    user?.verificationLevel && user.verificationLevel !== 'none'
      ? true
      : Boolean(user?.emailVerified && user?.phoneVerified);

  const hasDocumentsUploaded =
    user?.verification?.identity?.documentImages?.length > 0 ||
    user?.idDocumentUploaded;

  const rawIdStatus   = user?.verification?.identity?.status;
  const identityStatus = hasDocumentsUploaded
    ? rawIdStatus
    : (rawIdStatus === 'approved' ? 'approved' : 'none');

  const adminNotes = user?.verification?.identity?.adminNotes;

  // ── File handling ────────────────────────────────────────────────────────
  const handleFileSelect = (side, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [side]: 'File size must be less than 5MB' }));
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      setErrors(prev => ({ ...prev, [side]: 'Only JPG and PNG images are allowed' }));
      return;
    }
    setErrors(prev => { const n = { ...prev }; delete n[side]; return n; });

    const preview = URL.createObjectURL(file);
    if (side === 'front') { setFrontImage(file); setFrontPreview(preview); }
    else                  { setBackImage(file);  setBackPreview(preview);  }
  };

  const clearFile = (side) => {
    if (side === 'front') { setFrontImage(null); setFrontPreview(null); }
    else                  { setBackImage(null);  setBackPreview(null);  }
  };

  // ── Validation ───────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!idType)           errs.idType   = 'Please select an ID type';
    if (!frontImage)       errs.front    = 'Front image is required';
    if (!backImage && idType !== 'passport') errs.back = 'Back image is required';
    if (!fullName.trim())  errs.fullName = 'Full name as on ID is required';
    if (!idNumber.trim())  errs.idNumber = 'ID number is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) { toast.error('Please fill in all required fields'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('idType',      idType);
      fd.append('idNumber',    idNumber);
      fd.append('fullName',    fullName);
      fd.append('frontImage',  frontImage);
      if (backImage) fd.append('backImage', backImage);

      const token = localStorage.getItem('gigship_token');
      const res   = await fetch(`${import.meta.env.VITE_API_URL}/verification/identity`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || 'Submission failed');

      // Refresh user so verification level is current
      await refreshUser();

      const status = data.status || 'pending';
      setResultStatus(status);
      setStep(2);

      if (status === 'approved') {
        toast.success('Identity verified successfully!');
      } else {
        toast.success('Documents submitted — we will notify you by email.');
      }

      if (onSuccess) onSuccess(status);
    } catch (err) {
      console.error('Identity verification error:', err);
      toast.error(err.message || 'Failed to submit verification');
    } finally {
      setLoading(false);
    }
  };

  // ── Reset and close ──────────────────────────────────────────────────────
  const handleClose = () => {
    setStep(1); setIdType(''); setIdNumber(''); setFullName('');
    setFrontImage(null); setFrontPreview(null);
    setBackImage(null);  setBackPreview(null);
    setErrors({}); setResultStatus(null);
    onClose();
  };

  if (!isOpen) return null;

  // ── Outer overlay ─────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl">

        {/* ── Header ── */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Identity Verification</h2>
              <p className="text-xs text-gray-500">Upload a government-issued ID to unlock Secondary Verification</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">

          {/* ── Guard: ID verification temporarily paused (NIA) ── */}
          {idVerificationTemporarilyPaused && (
            <div className="flex flex-col items-center text-center py-8">
              <Info className="w-14 h-14 text-blue-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">ID Verification Temporarily Paused</h3>
              <p className="text-sm text-gray-600 mb-6">
                ID upload is on hold while we complete a regulatory integration. In the meantime, you can reach Secondary Verification instantly by verifying a bank account or mobile money account.
              </p>
              <Button onClick={handleClose} variant="secondary">Close</Button>
            </div>
          )}

          {/* ── Guard: Basic verification required ── */}
          {!idVerificationTemporarilyPaused && !isBasicVerified && (
            <div className="flex flex-col items-center text-center py-8">
              <AlertCircle className="w-14 h-14 text-red-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Basic Verification Required</h3>
              <p className="text-sm text-gray-600 mb-6">
                You must verify your email and phone number before completing identity verification.
              </p>
              <Button onClick={handleClose} variant="secondary">Close</Button>
            </div>
          )}

          {/* ── Guard: Currently pending ── */}
          {!idVerificationTemporarilyPaused && isBasicVerified && identityStatus === 'pending' && (
            <div className="flex flex-col items-center text-center py-8">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4 animate-pulse">
                <Shield className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Verification In Progress</h3>
              <p className="text-sm text-gray-600 mb-6">
                Your documents are under review. This usually takes 24–48 hours.
                We will notify you once completed.
              </p>
              <Button onClick={handleClose} variant="secondary">Close</Button>
            </div>
          )}

          {/* ── Step 2: Success / Pending result ── */}
          {!idVerificationTemporarilyPaused && isBasicVerified && identityStatus !== 'pending' && step === 2 && (
            <div className="flex flex-col items-center text-center py-6">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                resultStatus === 'approved' ? 'bg-green-100' : 'bg-yellow-100'
              }`}>
                {resultStatus === 'approved'
                  ? <CheckCircle className="w-9 h-9 text-green-600" />
                  : <Shield className="w-9 h-9 text-yellow-600" />
                }
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {resultStatus === 'approved' ? 'Identity Verified!' : 'Documents Submitted!'}
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                {resultStatus === 'approved'
                  ? 'You now have Secondary Verification status. You can transact freely on GigShip.'
                  : 'Our team will review your documents within 24–48 hours. You will receive an email notification.'
                }
              </p>
              {resultStatus !== 'approved' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left w-full">
                  <div className="flex items-start gap-3">
                    <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Our team will review your documents</li>
                      <li>• You'll receive an email with the result</li>
                      <li>• Once approved, payments and withdrawals are unlocked</li>
                    </ul>
                  </div>
                </div>
              )}
              <Button onClick={handleClose} variant="primary">Done</Button>
            </div>
          )}

          {/* ── Step 1: Upload form ── */}
          {!idVerificationTemporarilyPaused && isBasicVerified && identityStatus !== 'pending' && step === 1 && (
            <div className="space-y-6">

              {/* Rejection alert */}
              {identityStatus === 'rejected' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-red-900 mb-1">Previous Submission Rejected</h4>
                      <p className="text-sm text-red-800 mb-1">Reason:</p>
                      <div className="bg-white/60 rounded border border-red-100 px-3 py-2 text-sm text-red-900 font-medium">
                        "{adminNotes || 'Document verification failed.'}"
                      </div>
                      <p className="text-xs text-red-700 mt-2">Please review the notes and upload valid documents below.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Benefits banner */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                <div className="flex items-start gap-3">
                  <Shield className="w-6 h-6 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold mb-1">Why verify?</p>
                    <ul className="text-sm text-blue-50 space-y-0.5">
                      <li>✓ Unlock Secondary Verification badge</li>
                      <li>✓ Higher transaction limits (up to GH₵50,000)</li>
                      <li>✓ Increased trust and profile visibility</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* ID Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select ID Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {acceptedIDTypes.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setIdType(t.value)}
                      className={`p-3 border-2 rounded-xl text-left transition-all ${
                        idType === t.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-2xl block mb-1">{t.icon}</span>
                      <span className="text-sm font-medium text-gray-900">{t.label}</span>
                    </button>
                  ))}
                </div>
                {errors.idType && <p className="text-red-500 text-xs mt-1">{errors.idType}</p>}
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name (as shown on ID) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="e.g. Kwame Mensah"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
              </div>

              {/* ID Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                  placeholder="e.g. GHA-XXXXXXXXX-X"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                {errors.idNumber && <p className="text-red-500 text-xs mt-1">{errors.idNumber}</p>}
              </div>

              {/* Front Image */}
              <FileUploadZone
                label="Front of ID"
                required
                preview={frontPreview}
                error={errors.front}
                inputId="id-front-upload"
                onSelect={(e) => handleFileSelect('front', e)}
                onClear={() => clearFile('front')}
              />

              {/* Back Image (not for passport) */}
              {idType && idType !== 'passport' && (
                <FileUploadZone
                  label="Back of ID"
                  required
                  preview={backPreview}
                  error={errors.back}
                  inputId="id-back-upload"
                  onSelect={(e) => handleFileSelect('back', e)}
                  onClear={() => clearFile('back')}
                />
              )}

              {/* Notes */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 text-sm mb-2">📌 Important Notes</h4>
                <ul className="text-xs text-yellow-800 space-y-1">
                  <li>• Ensure all text on the ID is clearly visible</li>
                  <li>• Photos should be well-lit and in focus</li>
                  <li>• Do not edit or crop the images</li>
                  <li>• Your full name here must match your GigShip profile exactly</li>
                </ul>
              </div>

              {/* Submit */}
              <Button
                onClick={handleSubmit}
                variant="primary"
                size="lg"
                fullWidth
                loading={loading}
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit for Verification'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Small helper: drag-and-drop-style upload zone ──────────────────────────
const FileUploadZone = ({ label, required, preview, error, inputId, onSelect, onClear }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="border-2 border-dashed border-gray-300 rounded-xl p-5 text-center">
      {preview ? (
        <div className="relative inline-block">
          <img src={preview} alt={label} className="max-h-48 mx-auto rounded-lg object-contain" />
          <button
            type="button"
            onClick={onClear}
            className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div>
          <Upload className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500 mb-1">Click to upload {label.toLowerCase()}</p>
          <p className="text-xs text-gray-400">JPG or PNG, max 5MB</p>
          <input
            type="file"
            accept="image/*"
            onChange={onSelect}
            className="hidden"
            id={inputId}
          />
          <label
            htmlFor={inputId}
            className="mt-3 inline-block px-4 py-2 bg-blue-600 text-white text-sm rounded-lg cursor-pointer hover:bg-blue-700 transition-colors"
          >
            Choose File
          </label>
        </div>
      )}
    </div>
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

export default IdentityVerificationModal;
